import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import FileDownload from '@mui/icons-material/FileDownload';
import axiosService from 'utils/axios';

interface ExportResultDialogProps {
  open: boolean;
  exportId: string;
  status: 'completed' | 'failed';
  fileUrl: string | null;
  errorMessage: string | null;
  onClose: () => void;
}

// Export files are private in storage (backend#70), so they are fetched through the authenticated API
// And saved from a blob, instead of opening a public storage URL.
const saveBlob = (blob: Blob, filename: string) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
};

const ExportResultDialog: React.FC<ExportResultDialogProps> = ({
  open,
  exportId,
  status,
  fileUrl,
  errorMessage,
  onClose,
}) => {
  const isSuccess = status === 'completed';
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = () => {
    setIsDownloading(true);
    setDownloadError(null);
    axiosService
      .get(`/vehicles/export/${encodeURIComponent(exportId)}/download/`, { responseType: 'blob' })
      .then(res => saveBlob(res.data, `vehicles_export_${exportId}.csv`))
      .catch(() => setDownloadError('Download failed. Please try again or start a new export.'))
      .finally(() => setIsDownloading(false));
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{isSuccess ? 'Export Complete' : 'Export Failed'}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {isSuccess
            ? 'Your vehicle data export is ready for download.'
            : `Export failed: ${errorMessage || 'An unknown error occurred.'}`}
        </DialogContentText>
        {downloadError ? <DialogContentText color="error">{downloadError}</DialogContentText> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {isSuccess && fileUrl ? (
          <Button
            onClick={handleDownload}
            disabled={isDownloading}
            variant="contained"
            color="primary"
            startIcon={<FileDownload />}
          >
            {isDownloading ? 'Downloading...' : 'Download CSV'}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default ExportResultDialog;
